import type { Order } from '@/mocks/orders';

const steps: { step: Order['status']; label: string; icon: string }[] = [
  { step: 'confirmed', label: 'Order Confirmed', icon: 'ri-checkbox-circle-line' },
  { step: 'production', label: 'In Production', icon: 'ri-hammer-line' },
  { step: 'quality_check', label: 'Quality Check', icon: 'ri-shield-check-line' },
  { step: 'shipped', label: 'Shipped', icon: 'ri-ship-line' },
  { step: 'arrived', label: 'Arrived', icon: 'ri-map-pin-2-line' },
];

const statusOrder: Order['status'][] = ['confirmed', 'production', 'quality_check', 'shipped', 'arrived'];

interface Props {
  order: Order;
}

export default function OrderTimeline({ order }: Props) {
  const currentIndex = statusOrder.indexOf(order.status);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
      <h3 className="font-black text-base mb-8" style={{ color: '#0a2342' }}>
        <i className="ri-route-line mr-2" />Shipment Progress
      </h3>

      {/* Desktop timeline */}
      <div className="hidden md:flex items-start relative mb-8">
        {/* Progress line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" />
        <div
          className="absolute top-5 left-0 h-0.5 z-0 transition-all duration-700"
          style={{
            backgroundColor: '#0a2342',
            width: `${(currentIndex / (steps.length - 1)) * 100}%`,
          }}
        />

        {steps.map((s, i) => {
          const done = i <= currentIndex;
          const active = i === currentIndex;
          const tl = order.timeline.find((t) => t.step === s.step);

          return (
            <div key={s.step} className="flex-1 flex flex-col items-center relative z-10">
              {/* Circle */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  done ? 'border-transparent text-white' : 'border-gray-200 bg-white text-gray-300'
                } ${active ? 'ring-4' : ''}`}
                style={{
                  backgroundColor: done ? '#0a2342' : 'white',
                  ringColor: done ? 'rgba(10,35,66,0.15)' : 'transparent',
                  boxShadow: active ? '0 0 0 4px rgba(10,35,66,0.12)' : 'none',
                }}
              >
                <i className={`${s.icon} text-sm`} />
              </div>

              {/* Label */}
              <div className={`mt-3 text-center px-1 ${done ? 'font-bold' : 'font-medium text-gray-400'}`}
                style={{ color: done ? '#0a2342' : undefined, fontSize: '12px' }}>
                {s.label}
              </div>

              {/* Date */}
              {tl && (
                <div className="text-xs text-gray-400 mt-1 text-center px-1 leading-tight">
                  {tl.date}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile timeline — vertical */}
      <div className="md:hidden space-y-0">
        {order.timeline.map((tl, i) => {
          const stepMeta = steps.find((s) => s.step === tl.step);
          const isLast = i === order.timeline.length - 1;

          return (
            <div key={tl.step} className="flex gap-4">
              {/* Left: icon + line */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                    tl.done ? 'border-transparent text-white' : 'border-gray-200 bg-white text-gray-300'
                  }`}
                  style={{ backgroundColor: tl.done ? '#0a2342' : 'white', boxShadow: tl.done && i === statusOrder.indexOf(order.status) ? '0 0 0 4px rgba(10,35,66,0.12)' : 'none' }}
                >
                  <i className={`${stepMeta?.icon} text-sm`} />
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 min-h-[24px] ${tl.done ? '' : 'bg-gray-200'}`}
                    style={{ backgroundColor: tl.done ? '#0a2342' : undefined }} />
                )}
              </div>

              {/* Right: content */}
              <div className={`pb-6 ${isLast ? 'pb-0' : ''}`}>
                <div className={`font-bold text-sm ${tl.done ? '' : 'text-gray-400'}`}
                  style={{ color: tl.done ? '#0a2342' : undefined }}>
                  {tl.label}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{tl.date}</div>
                {tl.note && (
                  <div className="text-xs text-gray-500 mt-1 leading-relaxed">{tl.note}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop notes */}
      <div className="hidden md:block mt-2 space-y-2">
        {order.timeline.filter((t) => t.note && t.done).map((tl) => (
          <div key={tl.step} className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-2.5">
            <i className="ri-information-line flex-shrink-0 mt-0.5" style={{ color: '#0a2342' }} />
            <span><strong className="text-gray-700">{order.timeline.find(t => t.step === tl.step)?.label}:</strong> {tl.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
